import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { ToastContainer } from '../shared/ToastContainer';
import { useThemeStore } from '../../stores/themeStore';

const SIDEBAR_WIDTH = 280;

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

export function AppLayout() {
  const isDesktop = useIsDesktop();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const { theme } = useThemeStore();

  // Apply / remove the `dark` class on <html> whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
  }, [theme]);

  useEffect(() => {
    if (isDesktop) setSidebarOpen(true);
    else setSidebarOpen(false);
  }, [isDesktop]);

  // Sidebar is persistently visible only on desktop when open
  const sidebarPersistent = isDesktop && sidebarOpen;

  return (
    <div
      className="min-h-screen bg-saas-bg"
      style={sidebarPersistent
        ? { background: theme === 'dark'
            ? 'linear-gradient(180deg, #0F172A 0%, #1E3A8A 100%)'
            : 'linear-gradient(180deg, #1E3A8A 0%, #2563EB 100%)'
          }
        : undefined}
    >
      {/* Mobile overlay backdrop */}
      {!isDesktop && sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isMobile={!isDesktop}
      />

      <TopBar
        onToggleSidebar={() => setSidebarOpen(v => !v)}
        sidebarOpen={sidebarOpen}
        sidebarPersistent={sidebarPersistent}
      />

      <ToastContainer />

      <main
        className="min-h-screen bg-saas-bg"
        style={{
          marginLeft: sidebarPersistent ? SIDEBAR_WIDTH : 0,
          paddingTop: 'var(--topbar-height)',
          borderTopLeftRadius: sidebarPersistent ? '1rem' : 0,
          transition: 'margin-left 300ms ease, border-top-left-radius 300ms ease',
        }}
      >
        <div className="p-4 sm:p-5 lg:p-6 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
